import Service from '@/service/api'
import i18n from '@/i18n'
import store from '@/store/store'
import { isAlertEnabled, maybeShowDesktopNotification } from '@/utils/workspace-preferences'
import { isReadyForAdminFinalApproval } from '@/utils/review-final-approval'

const STORAGE_KEY = 'workflow_notifications_v1'
const CHANGE_EVENT = 'workflow-notifications-changed'
const QUEUE_CACHE_MAX_AGE = 60000
const NOTIFICATION_ROLES = ['admin', 'committee', 'user']
const queueCache = new Map()
const USER_STAGE_LABELS = {
  en: {
    submitted: 'Waiting for Admin Review',
    suggestion: 'Under Committee Review',
    'final-review': 'Waiting for Admin Final Decision',
    approved: 'Approved',
    revision: 'Returned for Revision'
  },
  th: {
    submitted: 'รอการตรวจสอบจากผู้ดูแลระบบ',
    suggestion: 'อยู่ระหว่างการพิจารณาของคณะกรรมการ',
    'final-review': 'รอการตัดสินใจขั้นสุดท้ายจากผู้ดูแลระบบ',
    approved: 'อนุมัติแล้ว',
    revision: 'ส่งกลับเพื่อแก้ไข'
  }
}
const NOTIFICATION_TEXT = {
  en: {
    common: {
      workflowUpdate: 'Workflow update',
      workflowSource: 'Workflow',
      open: 'Open',
      openRelatedPage: 'Open the related page.',
      committeeReviewDefault: 'Committee review',
      yourAgreementDefault: 'Your agreement',
      agreementDefault: 'Agreement',
      anyAgreementDefault: 'An agreement'
    },
    sources: {
      committeeReview: 'Committee Review',
      reviewHistory: 'Review History',
      myAgreements: 'My Agreements',
      myReviewQueue: 'My Review Queue',
      reviewQueue: 'Review Queue',
      translationRequests: 'Translation Requests',
      myTranslationRequests: 'My Translation Requests'
    },
    actions: {
      openReply: 'Open reply',
      openReview: 'Open review',
      openHistory: 'Open history',
      viewApprovedAgreement: 'View approved agreement',
      reviewFeedback: 'Review feedback',
      openComments: 'Open comments',
      openAgreement: 'Open agreement',
      openFinalApproval: 'Open final approval',
      openTranslationQueue: 'Open translation queue',
      openTranslatedDocument: 'Open translated document'
    },
    committee: {
      userReplyTitle: 'User replied to your comment',
      userReplyMessage(agreement) {
        return `${agreement} has a new user reply in the review discussion.`
      },
      userReplyNextStep: 'Read the user reply and continue the committee discussion.',
      reviewItemTitle: 'New committee review item',
      reviewItemMessage(agreement) {
        return `${agreement} is waiting for committee review.`
      },
      reviewItemNextStep: 'Open the agreement and continue the committee review.',
      rejectedTitle: 'Agreement rejected by admin',
      rejectedMessage(agreement) {
        return `${agreement} was rejected by admin.`
      },
      rejectedNextStep: 'Open review history and check the admin rejection details.',
      approvedTitle: 'Admin gave final approval',
      approvedMessage(agreement) {
        return `${agreement} has been finally approved by admin.`
      },
      approvedNextStep: 'Check the final approved result in review history.'
    },
    user: {
      approvedTitle: 'Agreement approved by admin',
      approvedMessage(agreement) {
        return `${agreement} was approved by admin.`
      },
      approvedNextStep: 'Open the approved agreement and review the final status.',
      returnedTitle: 'Agreement returned by admin',
      returnedMessage(agreement, note) {
        return note
          ? `${agreement} was returned by admin: ${note}`
          : `${agreement} was returned by admin and needs revision.`
      },
      returnedNextStep: 'Review the admin decision, revise the draft, and resubmit.',
      committeeCommentTitle: 'Committee commented on your agreement',
      committeeCommentMessage(agreement) {
        return `${agreement} has a new committee comment.`
      },
      committeeCommentNextStep: 'Read the committee comment and continue the review.',
      workflowUpdateTitle: 'Agreement workflow update',
      workflowUpdateMessage(agreement, stage) {
        return `${agreement} is currently ${stage}.`
      },
      workflowUpdateNextStep: 'Open the agreement and check the latest workflow status.',
      translationReadyTitle: 'Translation completed',
      translationReadyMessage(agreement) {
        return `${agreement} has a completed translated document ready to download.`
      },
      translationReadyNextStep: 'Open your translation queue and download the translated DOCX.',
      translationRejectedTitle: 'Translation request returned',
      translationRejectedMessage(agreement) {
        return `${agreement} translation request was returned.`
      },
      translationRejectedNextStep: 'Open your translation queue and review the returned request.'
    },
    admin: {
      finalApprovalTitle: 'Document received for final approval',
      finalApprovalMessage(agreement) {
        return `${agreement} has all committee approvals and is ready for final admin approval.`
      },
      finalApprovalNextStep: 'Review the document and submit the final admin decision.',
      submittedTitle: 'Agreement submitted for review',
      submittedMessage(agreement) {
        return `${agreement} was submitted and has entered the review queue.`
      },
      committeeProgressTitle: 'Committee review in progress',
      committeeProgressMessage(agreement) {
        return `${agreement} is moving through committee review and may need admin attention soon.`
      },
      finalApprovalMonitorNextStep: 'Open the final approval page and monitor committee progress.',
      reviewProgressNextStep: 'Open the review and monitor the workflow progress.',
      translationRequestTitle: 'Translation request waiting',
      translationRequestMessage(agreement, requester) {
        return requester
          ? `${agreement} was requested for translation by ${requester}.`
          : `${agreement} is waiting for translation.`
      },
      translationRequestNextStep: 'Open translation requests and run or review the translation.'
    }
  },
  th: {
    common: {
      workflowUpdate: 'อัปเดตเวิร์กโฟลว์',
      workflowSource: 'เวิร์กโฟลว์',
      open: 'เปิด',
      openRelatedPage: 'เปิดหน้าที่เกี่ยวข้อง',
      committeeReviewDefault: 'การรีวิวของคณะกรรมการ',
      yourAgreementDefault: 'ข้อตกลงของคุณ',
      agreementDefault: 'ข้อตกลง',
      anyAgreementDefault: 'มีข้อตกลงหนึ่งรายการ'
    },
    sources: {
      committeeReview: 'รีวิวคณะกรรมการ',
      reviewHistory: 'ประวัติการรีวิว',
      myAgreements: 'ข้อตกลงของฉัน',
      myReviewQueue: 'คิวรีวิวของฉัน',
      reviewQueue: 'คิวรีวิว',
      translationRequests: 'คำขอแปลภาษา',
      myTranslationRequests: 'คำขอแปลภาษาของฉัน'
    },
    actions: {
      openReply: 'เปิดคำตอบกลับ',
      openReview: 'เปิดรีวิว',
      openHistory: 'เปิดประวัติ',
      viewApprovedAgreement: 'ดูข้อตกลงที่อนุมัติแล้ว',
      reviewFeedback: 'ดูข้อเสนอแนะ',
      openComments: 'เปิดความคิดเห็น',
      openAgreement: 'เปิดข้อตกลง',
      openFinalApproval: 'เปิดการอนุมัติขั้นสุดท้าย',
      openTranslationQueue: 'เปิดคิวแปลภาษา',
      openTranslatedDocument: 'เปิดเอกสารที่แปลแล้ว'
    },
    committee: {
      userReplyTitle: 'ผู้ใช้ตอบกลับความคิดเห็นของคุณ',
      userReplyMessage(agreement) {
        return `${agreement} มีคำตอบกลับใหม่ในบทสนทนาการรีวิว`
      },
      userReplyNextStep: 'อ่านคำตอบกลับของผู้ใช้และดำเนินการรีวิวต่อ',
      reviewItemTitle: 'มีงานรีวิวใหม่ของคณะกรรมการ',
      reviewItemMessage(agreement) {
        return `${agreement} กำลังรอการรีวิวจากคณะกรรมการ`
      },
      reviewItemNextStep: 'เปิดข้อตกลงและดำเนินการรีวิวของคณะกรรมการต่อ',
      rejectedTitle: 'ข้อตกลงถูกผู้ดูแลระบบปฏิเสธ',
      rejectedMessage(agreement) {
        return `${agreement} ถูกปฏิเสธโดยผู้ดูแลระบบ`
      },
      rejectedNextStep: 'เปิดประวัติการรีวิวและตรวจสอบรายละเอียดการปฏิเสธจากผู้ดูแลระบบ',
      approvedTitle: 'ผู้ดูแลระบบอนุมัติขั้นสุดท้ายแล้ว',
      approvedMessage(agreement) {
        return `${agreement} ได้รับการอนุมัติขั้นสุดท้ายจากผู้ดูแลระบบแล้ว`
      },
      approvedNextStep: 'ตรวจสอบผลการอนุมัติขั้นสุดท้ายในประวัติการรีวิว'
    },
    user: {
      approvedTitle: 'ข้อตกลงได้รับอนุมัติจากผู้ดูแลระบบ',
      approvedMessage(agreement) {
        return `${agreement} ได้รับการอนุมัติจากผู้ดูแลระบบแล้ว`
      },
      approvedNextStep: 'เปิดข้อตกลงที่ได้รับอนุมัติและตรวจสอบสถานะสุดท้าย',
      returnedTitle: 'ผู้ดูแลระบบส่งข้อตกลงกลับมาแก้ไข',
      returnedMessage(agreement, note) {
        return note
          ? `${agreement} ถูกส่งกลับโดยผู้ดูแลระบบ: ${note}`
          : `${agreement} ถูกส่งกลับโดยผู้ดูแลระบบและต้องแก้ไขเพิ่มเติม`
      },
      returnedNextStep: 'ตรวจสอบคำตัดสินของผู้ดูแลระบบ แก้ไขร่าง และส่งใหม่',
      committeeCommentTitle: 'คณะกรรมการแสดงความคิดเห็นต่อข้อตกลงของคุณ',
      committeeCommentMessage(agreement) {
        return `${agreement} มีความคิดเห็นใหม่จากคณะกรรมการ`
      },
      committeeCommentNextStep: 'อ่านความคิดเห็นของคณะกรรมการและดำเนินการรีวิวต่อ',
      workflowUpdateTitle: 'อัปเดตเวิร์กโฟลว์ข้อตกลง',
      workflowUpdateMessage(agreement, stage) {
        return `${agreement} อยู่ในสถานะ ${stage}`
      },
      workflowUpdateNextStep: 'เปิดข้อตกลงและตรวจสอบสถานะเวิร์กโฟลว์ล่าสุด',
      translationReadyTitle: 'แปลเอกสารเสร็จแล้ว',
      translationReadyMessage(agreement) {
        return `${agreement} มีเอกสารแปลที่พร้อมดาวน์โหลดแล้ว`
      },
      translationReadyNextStep: 'เปิดคิวแปลภาษาของคุณและดาวน์โหลดไฟล์ DOCX ที่แปลแล้ว',
      translationRejectedTitle: 'คำขอแปลภาษาถูกส่งกลับ',
      translationRejectedMessage(agreement) {
        return `คำขอแปลภาษา ${agreement} ถูกส่งกลับ`
      },
      translationRejectedNextStep: 'เปิดคิวแปลภาษาของคุณและตรวจสอบคำขอที่ถูกส่งกลับ'
    },
    admin: {
      finalApprovalTitle: 'ได้รับเอกสารเพื่ออนุมัติขั้นสุดท้าย',
      finalApprovalMessage(agreement) {
        return `${agreement} ได้รับการอนุมัติจากคณะกรรมการครบแล้วและพร้อมสำหรับการอนุมัติขั้นสุดท้ายโดยผู้ดูแลระบบ`
      },
      finalApprovalNextStep: 'ตรวจสอบเอกสารและส่งคำตัดสินขั้นสุดท้ายของผู้ดูแลระบบ',
      submittedTitle: 'มีการส่งข้อตกลงเข้ามาเพื่อรีวิว',
      submittedMessage(agreement) {
        return `${agreement} ถูกส่งเข้ามาและเข้าสู่คิวรีวิวแล้ว`
      },
      committeeProgressTitle: 'การรีวิวของคณะกรรมการกำลังดำเนินการ',
      committeeProgressMessage(agreement) {
        return `${agreement} กำลังอยู่ในกระบวนการรีวิวของคณะกรรมการและอาจต้องการการติดตามจากผู้ดูแลระบบเร็ว ๆ นี้`
      },
      finalApprovalMonitorNextStep: 'เปิดหน้าการอนุมัติขั้นสุดท้ายและติดตามความคืบหน้าของคณะกรรมการ',
      reviewProgressNextStep: 'เปิดรีวิวและติดตามความคืบหน้าของเวิร์กโฟลว์',
      translationRequestTitle: 'มีคำขอแปลภาษารออยู่',
      translationRequestMessage(agreement, requester) {
        return requester
          ? `${agreement} ถูกขอให้แปลโดย ${requester}`
          : `${agreement} กำลังรอการแปลภาษา`
      },
      translationRequestNextStep: 'เปิดคำขอแปลภาษาและดำเนินการแปลหรือตรวจสอบ'
    }
  }
}

function safeWindow() {
  return typeof window !== 'undefined' ? window : null
}

function notificationLocaleCode() {
  return String(i18n && i18n.locale ? i18n.locale : 'en').toLowerCase().startsWith('th') ? 'th' : 'en'
}

function notificationText() {
  return NOTIFICATION_TEXT[notificationLocaleCode()] || NOTIFICATION_TEXT.en
}

function localizedUserStageLabel(stage, fallbackLabel = '') {
  const locale = notificationLocaleCode()
  const normalizedStage = String(stage || '').trim().toLowerCase()
  const labelMap = USER_STAGE_LABELS[locale] || USER_STAGE_LABELS.en
  if (labelMap[normalizedStage]) return labelMap[normalizedStage]
  return fallbackLabel || USER_STAGE_LABELS.en[normalizedStage] || USER_STAGE_LABELS.en.submitted
}

function normalizeIdentityValue(value) {
  return String(value || '').trim().toLowerCase()
}

function storageKeyForPrimaryKey(primaryKey) {
  const normalized = normalizeIdentityValue(primaryKey)
  return normalized
    ? `${STORAGE_KEY}:${encodeURIComponent(normalized)}`
    : STORAGE_KEY
}

function collectActorIdentityKeys(target, value) {
  if (!value) return
  if (typeof value === 'string' || typeof value === 'number') {
    const normalized = normalizeIdentityValue(value)
    if (normalized) target.add(normalized)
    return
  }
  if (Array.isArray(value)) {
    value.forEach(item => collectActorIdentityKeys(target, item))
    return
  }
  if (typeof value === 'object') {
    ;[
      value._id,
      value.id,
      value.email,
      value.code,
      value.authen,
      value.username,
      value.accountId,
      value.ownerAccount
    ].forEach(item => collectActorIdentityKeys(target, item))
  }
}

function currentActorContext() {
  const keys = new Set()
  const profile = store && store.getters ? (store.getters['auth/profile'] || null) : null
  collectActorIdentityKeys(keys, profile)

  const win = safeWindow()
  if (win && win.localStorage) {
    try {
      const raw = win.localStorage.getItem('objs')
      if (raw) {
        const data = JSON.parse(raw)
        ;[
          data,
          data && data.user,
          data && data.account,
          data && data.accounts,
          data && data.accounts && Array.isArray(data.accounts) ? data.accounts[0] : null
        ].forEach(item => collectActorIdentityKeys(keys, item))
      }
    } catch (error) {
      // Ignore malformed local workspace cache and fall back to profile-only scoping.
    }
  }

  const audienceKeys = [...keys]
  const primaryKey = audienceKeys.find(value => value.includes('@')) ||
    audienceKeys.find(Boolean) ||
    ''

  return {
    audienceKeys,
    storageKey: storageKeyForPrimaryKey(primaryKey)
  }
}

function readItemsForStorageKey(storageKey) {
  const win = safeWindow()
  if (!win || !win.localStorage) return []
  try {
    const raw = win.localStorage.getItem(storageKey)
    return raw ? JSON.parse(raw) : []
  } catch (error) {
    return []
  }
}

function readItems() {
  return readItemsForStorageKey(currentActorContext().storageKey)
}

function writeItemsForStorageKey(storageKey, items, options = {}) {
  const win = safeWindow()
  if (!win || !win.localStorage) return
  const emitChange = options && options.emitChange !== false
  win.localStorage.setItem(storageKey, JSON.stringify(items))
  if (storageKey !== STORAGE_KEY) {
    win.localStorage.removeItem(STORAGE_KEY)
  }
  if (emitChange) {
    win.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: { count: items.length } }))
  }
}

function writeItems(items, options = {}) {
  writeItemsForStorageKey(currentActorContext().storageKey, items, options)
}

function resolveNotificationStorageKeys(audienceKeys = [], fallbackAudienceKey = '') {
  const normalizedAudienceKeys = normalizeAudienceKeys(audienceKeys)
  const fallbackKey = normalizeIdentityValue(fallbackAudienceKey)
  const targetKeys = normalizedAudienceKeys.length
    ? normalizedAudienceKeys
    : (fallbackKey ? [fallbackKey] : [])
  const storageKeys = targetKeys
    .map(key => storageKeyForPrimaryKey(key))
    .filter(Boolean)
  return storageKeys.length
    ? [...new Set(storageKeys)]
    : [currentActorContext().storageKey]
}

function nextId() {
  return `wf-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`
}

function buildNotificationDedupeKey(parts = []) {
  return parts.map(part => String(part || '').trim()).join('|')
}

function notificationRouteKey(item) {
  if (!(item && typeof item === 'object')) return ''
  const role = String(item.role || '').trim().toLowerCase()
  const reference = String(item.reference || '').trim().toLowerCase()
  const targetPath = String(item.targetPath || '').trim().toLowerCase()
  if (!role || (!reference && !targetPath)) return ''
  return [role, reference, targetPath].join('|')
}

function notificationReferenceKey(item) {
  if (!(item && typeof item === 'object')) return ''
  const role = String(item.role || '').trim().toLowerCase()
  const reference = String(item.reference || '').trim().toLowerCase()
  if (!role || !reference) return ''
  return [role, reference].join('|')
}

function normalizeAudienceKeys(values) {
  return Array.isArray(values)
    ? values.map(value => String(value || '').trim().toLowerCase()).filter(Boolean)
    : []
}

function ensureNotificationAudience(item, fallbackKeys = []) {
  if (!(item && typeof item === 'object')) return item
  const audienceKey = String(item.audienceKey || '').trim().toLowerCase()
  const audienceKeys = normalizeAudienceKeys(item.audienceKeys)
  const fallbackAudienceKeys = normalizeAudienceKeys(fallbackKeys)
  const nextAudienceKeys = audienceKeys.length
    ? audienceKeys
    : (audienceKey ? [audienceKey] : fallbackAudienceKeys)

  return {
    ...item,
    audienceKey: audienceKey || (nextAudienceKeys[0] || ''),
    audienceKeys: nextAudienceKeys
  }
}

function latestCommentIdentity(comment) {
  if (!(comment && typeof comment === 'object')) return ''
  return String(
    comment.commentId ||
    comment.threadId ||
    comment.id ||
    comment.time ||
    comment.date ||
    comment.createdAt ||
    ''
  ).trim()
}

function parseTimestamp(value) {
  if (!value) return 0
  if (value instanceof Date) {
    const time = value.getTime()
    return Number.isNaN(time) ? 0 : time
  }
  if (typeof value === 'number') return Number.isFinite(value) ? value : 0

  const raw = String(value).trim()
  if (!raw) return 0

  const direct = new Date(raw).getTime()
  if (!Number.isNaN(direct)) return direct

  const normalized = raw.replace(' ', 'T')
  const normalizedTime = new Date(normalized).getTime()
  if (!Number.isNaN(normalizedTime)) return normalizedTime

  return 0
}

function objectIdTimestamp(value) {
  const raw = String(value || '').trim()
  const match = raw.match(/\b([a-f0-9]{24})\b/i)
  if (!match) return ''
  const seconds = parseInt(match[1].slice(0, 8), 16)
  if (!Number.isFinite(seconds)) return ''
  const date = new Date(seconds * 1000)
  return Number.isNaN(date.getTime()) ? '' : date.toISOString()
}

function normalizeTranslationStatus(value) {
  const status = String(value || '').trim().toLowerCase()
  if (status === 'requested') return 'pending'
  return status || 'pending'
}

function translationRequestId(item) {
  return String(item && (item._id || item.id) || '').trim()
}

function translationTimestamp(item, fallbackId = '') {
  return (
    item && item.completedAt ||
    item && item.update && item.update.datetime ||
    item && item.requestedAt ||
    item && item.create && item.create.datetime ||
    item && item.createdAt ||
    objectIdTimestamp(fallbackId) ||
    new Date().toISOString()
  )
}

function translationLanguageLabel(value) {
  const map = {
    th: 'Thai',
    thai: 'Thai',
    zh: 'Chinese',
    chinese: 'Chinese',
    cn: 'Chinese',
    ja: 'Japanese',
    japanese: 'Japanese',
    jp: 'Japanese',
    ko: 'Korean',
    korean: 'Korean',
    kr: 'Korean',
    fr: 'French',
    french: 'French'
  }
  const raw = String(value || '').trim()
  return map[raw.toLowerCase()] || raw || 'Translation'
}

function resolvePreferenceChannel(item) {
  const type = String(item && item.type ? item.type : '').trim().toLowerCase()
  const targetPath = String(item && item.targetPath ? item.targetPath : '').trim().toLowerCase()
  if (type === 'translation' || targetPath.includes('/translation')) return 'translation'
  if (type === 'review' || targetPath.includes('/review')) return 'review'
  return ''
}

function currentAudienceKeys() {
  return currentActorContext().audienceKeys
}

export function createWorkflowNotification(payload) {
  const currentKeys = currentAudienceKeys()
  const text = notificationText()
  const explicitAudienceKeys = Array.isArray(payload && payload.audienceKeys)
    ? payload.audienceKeys.map(item => String(item).trim().toLowerCase()).filter(Boolean)
    : []
  const audienceKey = payload && payload.audienceKey
    ? String(payload.audienceKey).trim().toLowerCase()
    : ''
  const audienceKeys = explicitAudienceKeys.length
    ? explicitAudienceKeys
    : (audienceKey ? [audienceKey] : currentKeys)
  const nextItem = ensureNotificationAudience({
    id: nextId(),
    dedupeKey: payload && payload.dedupeKey ? payload.dedupeKey : '',
    role: payload && payload.role ? payload.role : 'user',
    type: payload && payload.type ? payload.type : 'review',
    title: payload && payload.title ? payload.title : text.common.workflowUpdate,
    message: payload && payload.message ? payload.message : '',
    source: payload && payload.source ? payload.source : text.common.workflowSource,
    partnerName: payload && payload.partnerName ? payload.partnerName : '',
    reference: payload && payload.reference ? payload.reference : '',
    audienceKey,
    audienceKeys,
    priority: payload && payload.priority ? payload.priority : 'medium',
    timestamp: new Date().toISOString(),
    dueAt: payload && payload.dueAt ? payload.dueAt : null,
    targetPath: payload && payload.targetPath ? payload.targetPath : '/notifications',
    actionLabel: payload && payload.actionLabel ? payload.actionLabel : text.common.open,
    nextStep: payload && payload.nextStep ? payload.nextStep : text.common.openRelatedPage,
    read: false
  }, currentKeys)
  const channel = resolvePreferenceChannel(nextItem)
  if (channel && !isAlertEnabled(channel)) {
    return nextItem
  }
  const currentStorageKey = currentActorContext().storageKey
  const targetStorageKeys = resolveNotificationStorageKeys(nextItem.audienceKeys, nextItem.audienceKey)
  targetStorageKeys.forEach(storageKey => {
    const items = readItemsForStorageKey(storageKey)
    writeItemsForStorageKey(storageKey, [nextItem, ...items].slice(0, 200), {
      emitChange: storageKey === currentStorageKey
    })
  })
  if (targetStorageKeys.includes(currentStorageKey)) {
    maybeShowDesktopNotification({
      title: nextItem.title,
      message: nextItem.message,
      onClick: () => {
        markWorkflowNotificationRead(nextItem.id, nextItem)
        const win = safeWindow()
        if (!win) return
        if (typeof win.focus === 'function') win.focus()
        if (nextItem.targetPath) {
          win.location.assign(nextItem.targetPath)
        }
      }
    })
  }
  return nextItem
}

export function listWorkflowNotifications(role, options = {}) {
  const audienceKeys = currentAudienceKeys()
  const items = readItems().map(item => ensureNotificationAudience(item, audienceKeys))
  const includeDismissed = !!(options && options.includeDismissed)
  const visibleItems = includeDismissed
    ? items
    : items.filter(item => !(item && item.dismissed))
  if (!role) return visibleItems
  return visibleItems.filter(item => {
    const channel = resolvePreferenceChannel(item)
    if (channel && !isAlertEnabled(channel)) return false
    if (item.role !== role) return false
    const itemKeys = normalizeAudienceKeys(item.audienceKeys)
    if (!itemKeys.length) return false
    if (!audienceKeys.length) return false
    return audienceKeys.some(value => itemKeys.includes(value))
  })
}

function normalizeNotificationRoles(roles) {
  const source = Array.isArray(roles) ? roles : [roles]
  const normalizedRoles = []
  source.forEach((role) => {
    const normalizedRole = String(role || '').trim().toLowerCase()
    if (normalizedRole === 'all' || normalizedRole === 'multiuser') {
      NOTIFICATION_ROLES.forEach((item) => {
        if (!normalizedRoles.includes(item)) normalizedRoles.push(item)
      })
      return
    }
    if (NOTIFICATION_ROLES.includes(normalizedRole) && !normalizedRoles.includes(normalizedRole)) {
      normalizedRoles.push(normalizedRole)
    }
  })
  return normalizedRoles
}

export function listWorkflowNotificationsForRoles(roles, options = {}) {
  const normalizedRoles = normalizeNotificationRoles(roles)
  if (!normalizedRoles.length) return []
  return dedupeWorkflowNotifications(
    normalizedRoles.flatMap(role => listWorkflowNotifications(role, options))
  )
}

export function unreadWorkflowNotificationCount(role) {
  return listWorkflowNotifications(role).filter(item => !item.read).length
}

export function markWorkflowNotificationRead(id, fallbackItem = null) {
  const items = readItems()
  const fallbackKey = workflowNotificationKey(fallbackItem)
  const fallbackHasDedupeKey = hasNotificationDedupeKey(fallbackItem)
  const fallbackRouteKey = notificationRouteKey(fallbackItem)
  const fallbackReferenceKey = notificationReferenceKey(fallbackItem)
  const fallbackAudienceKeys = currentAudienceKeys()
  let found = false
  const nextItems = items.map(item => {
    const sameId = item.id === id
    const sameKey = fallbackKey && workflowNotificationKey(item) === fallbackKey
    const sameRouteKey = fallbackRouteKey && notificationRouteKey(item) === fallbackRouteKey
    const sameReferenceKey = fallbackReferenceKey && notificationReferenceKey(item) === fallbackReferenceKey
    const shouldMarkRead = fallbackHasDedupeKey
      ? sameKey
      : (sameId || sameKey || sameRouteKey || sameReferenceKey)
    if (!shouldMarkRead) return item
    found = true
    return ensureNotificationAudience({
      ...item,
      dedupeKey: (fallbackItem && fallbackItem.dedupeKey) ? fallbackItem.dedupeKey : item.dedupeKey,
      read: true
    }, fallbackAudienceKeys)
  })
  if (!found && fallbackItem && fallbackItem.id) {
    nextItems.unshift(ensureNotificationAudience({ ...fallbackItem, read: true }, fallbackAudienceKeys))
  }
  writeItems(nextItems.slice(0, 200))
}

export function dismissWorkflowNotification(id, fallbackItem = null) {
  const items = readItems()
  const fallbackKey = workflowNotificationKey(fallbackItem)
  const fallbackHasDedupeKey = hasNotificationDedupeKey(fallbackItem)
  const fallbackRouteKey = notificationRouteKey(fallbackItem)
  const fallbackReferenceKey = notificationReferenceKey(fallbackItem)
  const fallbackAudienceKeys = currentAudienceKeys()
  const dismissedAt = new Date().toISOString()
  let found = false
  const nextItems = items.map(item => {
    const sameId = item.id === id
    const sameKey = fallbackKey && workflowNotificationKey(item) === fallbackKey
    const sameRouteKey = fallbackRouteKey && notificationRouteKey(item) === fallbackRouteKey
    const sameReferenceKey = fallbackReferenceKey && notificationReferenceKey(item) === fallbackReferenceKey
    const shouldDismiss = fallbackHasDedupeKey
      ? sameKey
      : (sameId || sameKey || sameRouteKey || sameReferenceKey)
    if (!shouldDismiss) return item
    found = true
    return ensureNotificationAudience({
      ...item,
      dedupeKey: (fallbackItem && fallbackItem.dedupeKey) ? fallbackItem.dedupeKey : item.dedupeKey,
      dismissed: true,
      dismissedAt,
      read: true
    }, fallbackAudienceKeys)
  })
  if (!found && fallbackItem && fallbackItem.id) {
    nextItems.unshift(ensureNotificationAudience({
      ...fallbackItem,
      dismissed: true,
      dismissedAt,
      read: true
    }, fallbackAudienceKeys))
  }
  writeItems(nextItems.slice(0, 200))
}

export function markAllWorkflowNotificationsRead(role) {
  const items = readItems()
  writeItems(items.map(item => (role && item.role !== role ? item : { ...item, read: true })))
}

export function markAllWorkflowNotificationsReadForRoles(roles) {
  const normalizedRoles = normalizeNotificationRoles(roles)
  if (!normalizedRoles.length) return
  const roleSet = new Set(normalizedRoles)
  const items = readItems()
  writeItems(items.map(item => (roleSet.has(item && item.role) ? { ...item, read: true } : item)))
}

export function clearWorkflowNotifications(role) {
  const items = readItems()
  writeItems(role ? items.filter(item => item.role !== role) : [])
}

export function workflowNotificationEventName() {
  return CHANGE_EVENT
}

export function workflowNotificationKey(item) {
  if (item && item.dedupeKey) return String(item.dedupeKey)
  const role = item && item.role ? item.role : ''
  const type = item && item.type ? item.type : ''
  const reference = item && item.reference ? item.reference : ''
  if (role && type && reference) {
    return [role, type, reference].join('|')
  }
  return [
    role,
    type,
    reference,
    item && item.title ? item.title : '',
    item && item.targetPath ? item.targetPath : ''
  ].join('|')
}

function hasNotificationDedupeKey(item) {
  return Boolean(item && String(item.dedupeKey || '').trim())
}

export function workflowNotificationMatches(left, right) {
  if (!(left && right)) return false
  const leftKey = workflowNotificationKey(left)
  const rightKey = workflowNotificationKey(right)
  if (leftKey && rightKey && leftKey === rightKey) return true
  if (hasNotificationDedupeKey(left) || hasNotificationDedupeKey(right)) return false
  if (left.id && right.id && String(left.id) === String(right.id)) return true
  const leftRouteKey = notificationRouteKey(left)
  const rightRouteKey = notificationRouteKey(right)
  if (leftRouteKey && rightRouteKey && leftRouteKey === rightRouteKey) return true
  const leftReferenceKey = notificationReferenceKey(left)
  const rightReferenceKey = notificationReferenceKey(right)
  return Boolean(leftReferenceKey && rightReferenceKey && leftReferenceKey === rightReferenceKey)
}

export function mergeStoredWorkflowNotificationState(queueItems, storedItems) {
  const storedByKey = new Map()
  const storedById = new Map()
  const storedByRouteKey = new Map()
  const storedByReferenceKey = new Map()
  const storedByLegacySignature = new Map()
  ;(storedItems || []).forEach(item => {
    const key = workflowNotificationKey(item)
    if (!storedByKey.has(key)) {
      storedByKey.set(key, item)
    }
    if (item && item.id && !storedById.has(String(item.id))) {
      storedById.set(String(item.id), item)
    }
    const routeKey = notificationRouteKey(item)
    if (routeKey && !storedByRouteKey.has(routeKey)) {
      storedByRouteKey.set(routeKey, item)
    }
    const referenceKey = notificationReferenceKey(item)
    if (referenceKey && !storedByReferenceKey.has(referenceKey)) {
      storedByReferenceKey.set(referenceKey, item)
    }
    const legacySignature = [
      item && item.role ? item.role : '',
      item && item.type ? item.type : '',
      item && item.reference ? item.reference : '',
      item && item.targetPath ? item.targetPath : ''
    ].join('|')
    if (legacySignature !== '|||' && !storedByLegacySignature.has(legacySignature)) {
      storedByLegacySignature.set(legacySignature, item)
    }
  })
  return (queueItems || []).map(item => {
    const directKey = workflowNotificationKey(item)
    const legacySignature = [
      item && item.role ? item.role : '',
      item && item.type ? item.type : '',
      item && item.reference ? item.reference : '',
      item && item.targetPath ? item.targetPath : ''
    ].join('|')
    const routeKey = notificationRouteKey(item)
    const referenceKey = notificationReferenceKey(item)
    const storedMatch = hasNotificationDedupeKey(item)
      ? storedByKey.get(directKey)
      : (
          storedByKey.get(directKey) ||
          storedById.get(String(item && item.id ? item.id : '')) ||
          storedByRouteKey.get(routeKey) ||
          storedByReferenceKey.get(referenceKey) ||
          storedByLegacySignature.get(legacySignature)
        )
    if (!storedMatch) return item
    if (storedMatch.dismissed) return null
    return {
      ...item,
      dedupeKey: item && item.dedupeKey ? item.dedupeKey : storedMatch.dedupeKey,
      read: !!storedMatch.read
    }
  }).filter(Boolean)
}

export function dedupeWorkflowNotifications(items) {
  const deduped = []
  const seen = new Set()
  ;(items || []).forEach(item => {
    const key = workflowNotificationKey(item)
    if (seen.has(key)) return
    seen.add(key)
    deduped.push(item)
  })
  return deduped
}

function latestCommentTimestamp(comments = [], fallback = '') {
  const latestComment = Array.isArray(comments) && comments.length
    ? comments.reduce((latest, current) => {
        const latestValue = parseTimestamp(latest && (latest.time || latest.date || latest.createdAt))
        const currentValue = parseTimestamp(current && (current.time || current.date || current.createdAt))
        return currentValue >= latestValue ? current : latest
      }, comments[0])
    : null
  const latestCommentTime = parseTimestamp(latestComment && (latestComment.time || latestComment.date || latestComment.createdAt))
  const fallbackTime = parseTimestamp(fallback)
  return {
    latestComment,
    timestamp: latestCommentTime >= fallbackTime
      ? (latestComment && (latestComment.time || latestComment.date || latestComment.createdAt)) || fallback || ''
      : fallback || ''
  }
}

async function buildCommitteeNotifications() {
  try {
    const text = notificationText()
    const response = await Service.committeeReview('dashboard')
    const data = response && response.data && response.data.data ? response.data.data : {}
    const reviews = Array.isArray(data.reviews) ? data.reviews : []
    const liveItems = reviews
      .filter(item => item && item.status !== 'completed')
      .map(item => {
        const agreementId = item.agreementId || item.id
        const agreementTitle = item.agreement || item.title || text.common.committeeReviewDefault
        const comments = Array.isArray(item.comments) ? item.comments : []
        const fallbackTimestamp = item.updatedAt || item.submittedAt || item.createdAt || objectIdTimestamp(agreementId) || ''
        const { latestComment, timestamp } = latestCommentTimestamp(
          comments,
          fallbackTimestamp
        )
        const latestRole = String(latestComment && latestComment.role || '').toLowerCase()
        const hasUserReply = latestRole === 'user'
        const statusKey = String(item && item.status ? item.status : '').trim().toLowerCase()
        const latestCommentKey = latestCommentIdentity(latestComment)
        const dedupeKey = hasUserReply
          ? buildNotificationDedupeKey(['committee', agreementId, 'user-reply', latestCommentKey || timestamp])
          : buildNotificationDedupeKey(['committee', agreementId, statusKey || 'queue'])
        return {
          id: `committee-task-${agreementId}`,
          dedupeKey,
          role: 'committee',
          agreementName: agreementTitle,
          partnerName: item.partner || '',
          type: 'review',
          title: hasUserReply ? text.committee.userReplyTitle : text.committee.reviewItemTitle,
          message: hasUserReply
            ? text.committee.userReplyMessage(agreementTitle)
            : text.committee.reviewItemMessage(agreementTitle),
          source: text.sources.committeeReview,
          reference: agreementId ? `MOU-${agreementId}` : '',
          priority: hasUserReply ? 'high' : 'medium',
          timestamp,
          targetPath: agreementId ? `/review?agreementId=${agreementId}` : '/review',
          actionLabel: hasUserReply ? text.actions.openReply : text.actions.openReview,
          nextStep: hasUserReply
            ? text.committee.userReplyNextStep
            : text.committee.reviewItemNextStep
        }
      })

    const historyResponse = await Service.committeeReview('history')
    const historyItems = Array.isArray(historyResponse && historyResponse.data && historyResponse.data.data)
      ? historyResponse.data.data
      : []
    const historyNotifications = historyItems
      .filter(item => item && (item.decision === 'approved' || item.decision === 'rejected'))
      .map(item => {
        const isRejected = item.decision === 'rejected'
        const agreementTitle = item.agreement || text.common.anyAgreementDefault
        return {
          id: `committee-history-${item.id}-${item.decision}`,
          dedupeKey: buildNotificationDedupeKey(['committee-history', item.agreementId || item.id, item.decision, item.reviewDate || item.completedDate || item.updatedAt]),
          role: 'committee',
          agreementName: agreementTitle,
          partnerName: item.partner || '',
          type: isRejected ? 'review' : 'approval',
          title: isRejected ? text.committee.rejectedTitle : text.committee.approvedTitle,
          message: isRejected
            ? text.committee.rejectedMessage(agreementTitle)
            : text.committee.approvedMessage(agreementTitle),
          source: text.sources.reviewHistory,
          reference: item.id ? `MOU-${item.id}` : '',
          priority: isRejected ? 'high' : 'medium',
          timestamp: item.reviewDate || item.completedDate || item.updatedAt || new Date().toISOString(),
          targetPath: '/review-history',
          actionLabel: text.actions.openHistory,
          nextStep: isRejected
            ? text.committee.rejectedNextStep
            : text.committee.approvedNextStep
        }
      })

    return [...historyNotifications, ...liveItems]
  } catch (error) {
    return []
  }
}

async function buildUserTranslationNotifications(options = {}) {
  try {
    const text = notificationText()
    const response = await Service.userWorkspace('translation-requests', {}, {
      forceRefresh: !!options.forceRefresh
    })
    const requests = Array.isArray(response && response.data && response.data.data)
      ? response.data.data
      : []

    return requests
      .filter(item => {
        const status = normalizeTranslationStatus(item && item.status)
        return status === 'rejected'
      })
      .map(item => {
        const requestId = translationRequestId(item)
        const status = normalizeTranslationStatus(item && item.status)
        const agreementTitle = item.agreementTitle || text.common.yourAgreementDefault
        const timestamp = translationTimestamp(item, requestId)
        return {
          id: `user-translation-${requestId}-${status}`,
          dedupeKey: buildNotificationDedupeKey(['user-translation', requestId, status]),
          role: 'user',
          agreementName: agreementTitle,
          partnerName: item.partner || '',
          type: 'translation',
          title: text.user.translationRejectedTitle,
          message: text.user.translationRejectedMessage(agreementTitle),
          source: text.sources.myTranslationRequests,
          reference: requestId ? `TR-${requestId}` : '',
          priority: 'high',
          timestamp,
          targetPath: '/dashboard/user-translation?tab=requests&status=rejected',
          actionLabel: text.actions.openTranslatedDocument,
          nextStep: text.user.translationRejectedNextStep
        }
      })
  } catch (error) {
    return []
  }
}

async function buildUserNotifications(options = {}) {
  try {
    const text = notificationText()
    const response = await Service.agreements('review-queue')
    const data = response && response.data && response.data.data ? response.data.data : {}
    const pending = Array.isArray(data.pending) ? data.pending : []
    const history = Array.isArray(data.history) ? data.history : []
    const activeItems = [...pending, ...history].filter(item => item && item.derivedStage !== 'approved')
    const approvedItems = history
      .filter(item => item && item.derivedStage === 'approved')
      .map(item => ({
        id: `user-approved-${item.id}`,
        dedupeKey: buildNotificationDedupeKey(['user-approved', item.id, item.updatedAt || item.approvedAt || item.submittedAt || item.createdAt]),
        role: 'user',
        agreementName: item.title || text.common.yourAgreementDefault,
        partnerName: item.partner || '',
        type: 'approval',
        title: text.user.approvedTitle,
        message: text.user.approvedMessage(item.title || text.common.yourAgreementDefault),
        source: text.sources.myAgreements,
        reference: item.id ? `MOU-${item.id}` : '',
        priority: 'high',
        timestamp: item.updatedAt || item.approvedAt || item.submittedAt || item.createdAt || new Date().toISOString(),
        targetPath: `/dashboard/user-agreements/${item.id}/review-summary?mode=view`,
        actionLabel: text.actions.viewApprovedAgreement,
        nextStep: text.user.approvedNextStep
      }))

    const queueItems = activeItems.map(item => {
      const agreementId = item.id
      const workflow = item && item.workflow ? item.workflow : {}
      const workflowComments = Array.isArray(workflow.comments) ? workflow.comments : []
      const { latestComment, timestamp } = latestCommentTimestamp(workflowComments, item.updatedAt || item.submittedAt || item.createdAt)
      const latestRole = String(latestComment && latestComment.role || '').toLowerCase()
      const needsReviewSteps = item.derivedStage === 'submitted' || item.derivedStage === 'suggestion' || item.derivedStage === 'revision'
      const hasCommitteeComment = latestRole === 'committee'
      const hasAdminComment = latestRole === 'admin'
      const hasAdminRejection = item.derivedStage === 'revision' && (
        hasAdminComment ||
        String(workflow.decisionNote || '').trim()
      )
      const agreementTitle = item.title || text.common.yourAgreementDefault
      const currentStageLabel = localizedUserStageLabel(item.derivedStage, item.derivedStageLabel)
      const decisionNote = String(workflow.decisionNote || '').trim()

      return {
        id: `user-task-${agreementId}`,
        dedupeKey: buildNotificationDedupeKey(['user-task', agreementId, item.derivedStage, latestRole || 'workflow', timestamp]),
        role: 'user',
        agreementName: agreementTitle,
        partnerName: item.partner || '',
        type: needsReviewSteps ? 'review' : 'approval',
        title: hasAdminRejection
          ? text.user.returnedTitle
          : hasCommitteeComment
            ? text.user.committeeCommentTitle
            : text.user.workflowUpdateTitle,
        message: hasAdminRejection
          ? text.user.returnedMessage(agreementTitle, decisionNote)
          : hasCommitteeComment
            ? text.user.committeeCommentMessage(agreementTitle)
            : text.user.workflowUpdateMessage(agreementTitle, currentStageLabel),
        source: needsReviewSteps ? text.sources.myReviewQueue : text.sources.myAgreements,
        reference: agreementId ? `MOU-${agreementId}` : '',
        priority: needsReviewSteps ? 'high' : 'medium',
        timestamp,
        targetPath: needsReviewSteps
          ? `/dashboard/user-agreements/${agreementId}/start-review`
          : `/dashboard/user-agreements/${agreementId}/review-summary?mode=view`,
        actionLabel: hasAdminRejection ? text.actions.reviewFeedback : (hasCommitteeComment ? text.actions.openComments : text.actions.openAgreement),
        nextStep: hasAdminRejection
          ? text.user.returnedNextStep
          : hasCommitteeComment
            ? text.user.committeeCommentNextStep
            : text.user.workflowUpdateNextStep
      }
    })

    const translationItems = await buildUserTranslationNotifications(options)
    return [...approvedItems, ...queueItems, ...translationItems]
  } catch (error) {
    return buildUserTranslationNotifications(options)
  }
}

async function buildAdminTranslationNotifications(options = {}) {
  try {
    const text = notificationText()
    const response = await Service.translation('requests', {}, {
      forceRefresh: !!options.forceRefresh
    })
    const requests = Array.isArray(response && response.data && response.data.data)
      ? response.data.data
      : []

    return requests
      .filter(item => {
        const status = normalizeTranslationStatus(item && item.status)
        return status !== 'completed' && status !== 'rejected'
      })
      .map(item => {
        const requestId = translationRequestId(item)
        const agreementTitle = item.agreementTitle || text.common.agreementDefault
        const timestamp = translationTimestamp(item, requestId)
        const requester = item.requestedByName || item.requestedByEmail || ''
        const languageLabel = translationLanguageLabel(item.targetLanguage)
        return {
          id: `admin-translation-${requestId}`,
          dedupeKey: buildNotificationDedupeKey(['admin-translation', requestId, normalizeTranslationStatus(item.status), timestamp]),
          role: 'admin',
          agreementName: agreementTitle,
          partnerName: item.partner || '',
          type: 'translation',
          title: text.admin.translationRequestTitle,
          message: `${text.admin.translationRequestMessage(agreementTitle, requester)} (${languageLabel})`,
          source: text.sources.translationRequests,
          reference: requestId ? `TR-${requestId}` : '',
          priority: 'medium',
          timestamp,
          targetPath: '/translation-requests',
          actionLabel: text.actions.openTranslationQueue,
          nextStep: text.admin.translationRequestNextStep
        }
      })
  } catch (error) {
    return []
  }
}

async function buildAdminNotifications(options = {}) {
  try {
    const text = notificationText()
    const response = await Service.adminReview('queue')
    const reviews = Array.isArray(response && response.data && response.data.data)
      ? response.data.data
      : []
    const reviewItems = reviews
      .filter(item => item && item.status !== 'approved')
      .map(item => {
        const agreementId = item.id
        const currentStep = Number(item.currentStep || 1)
        const readyForAdminFinalApproval = isReadyForAdminFinalApproval(item)
        const isFinalApproval = readyForAdminFinalApproval
        const usesFinalApprovalPage = readyForAdminFinalApproval
        const isNewSubmission = currentStep <= 2
        const agreementTitle = item.title || text.common.agreementDefault
        return {
          id: `admin-task-${agreementId}`,
          dedupeKey: buildNotificationDedupeKey(['admin-task', agreementId, currentStep, item.committeeDecision || '', item.updatedAt || item.submittedAt || item.createdAt]),
          role: 'admin',
          agreementName: agreementTitle,
          partnerName: item.partner || '',
          type: isFinalApproval ? 'approval' : 'review',
          title: isFinalApproval
            ? text.admin.finalApprovalTitle
            : isNewSubmission
              ? text.admin.submittedTitle
              : text.admin.committeeProgressTitle,
          message: isFinalApproval
            ? text.admin.finalApprovalMessage(agreementTitle)
            : isNewSubmission
              ? text.admin.submittedMessage(agreementTitle)
              : text.admin.committeeProgressMessage(agreementTitle),
          source: text.sources.reviewQueue,
          reference: agreementId ? `MOU-${agreementId}` : '',
          priority: isFinalApproval ? 'high' : 'medium',
          timestamp: item.updatedAt || item.submittedAt || item.createdAt || new Date().toISOString(),
          targetPath: agreementId
            ? (usesFinalApprovalPage ? `/review-queue/${agreementId}/final-approval` : `/review?agreementId=${agreementId}`)
            : '/review-queue',
          actionLabel: usesFinalApprovalPage ? text.actions.openFinalApproval : text.actions.openReview,
          nextStep: isFinalApproval
            ? text.admin.finalApprovalNextStep
            : usesFinalApprovalPage
              ? text.admin.finalApprovalMonitorNextStep
            : text.admin.reviewProgressNextStep
        }
      })
    const translationItems = await buildAdminTranslationNotifications(options)
    return [...reviewItems, ...translationItems]
  } catch (error) {
    return buildAdminTranslationNotifications(options)
  }
}

export async function buildWorkflowNotificationsForRole(role, options = {}) {
  const normalizedRole = String(role || '').trim().toLowerCase()
  const cacheKey = `${normalizedRole}:${notificationLocaleCode()}`
  const maxAge = typeof options.cacheMaxAge === 'number' ? options.cacheMaxAge : QUEUE_CACHE_MAX_AGE
  const now = Date.now()
  const cached = queueCache.get(cacheKey)

  if (!options.forceRefresh && cached && cached.expiresAt > now) {
    return cached.promise
  }

  const promise = (async () => {
    if (normalizedRole === 'committee') return buildCommitteeNotifications()
    if (normalizedRole === 'user') return buildUserNotifications(options)
    if (normalizedRole === 'admin') return buildAdminNotifications(options)
    return []
  })().catch((error) => {
    queueCache.delete(cacheKey)
    throw error
  })

  queueCache.set(cacheKey, {
    promise,
    expiresAt: now + maxAge
  })

  return promise
}

export async function buildWorkflowNotificationsForRoles(roles, options = {}) {
  const normalizedRoles = normalizeNotificationRoles(roles)
  if (!normalizedRoles.length) return []
  const results = await Promise.all(normalizedRoles.map(role => buildWorkflowNotificationsForRole(role, options)))
  return dedupeWorkflowNotifications(results.flat())
}
